import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import DefaultPreview from '../assets/preview_.png';
import AxiosFileSender from '../lib/AxiosFileSender.js';
import AxiosInstance from './AxiosInstance.js';
import useAPIList from './useAPIList.js';
import useAPIListAllReturn from './useAPIListAllReturn.js';

export function asForm(training) {
  return {
    ...training,
    date: training.date.split('T')[0],
    endDate: training.endDate.split('T')[0],
  };
}

export function getURL(training) {
  return `/trainings/${training.id}`;
}

export function getURLSession(trainingId, sessionIndex, sessionItem) {
  if (!trainingId || !sessionIndex || !sessionItem) {
    console.warn('getURLSession: missing', {
      trainingId,
      sessionIndex,
      sessionItem,
    });
    return '/trainings';
  }
  return `/trainings/${trainingId}/${sessionIndex}/${sessionItem}video/lecture`;
}

export function getURLSessionQuiz(trainingId, sessionIndex, sessionItem) {
  return `/trainings/${trainingId}/${sessionIndex}/${sessionItem}quiz/lecture`;
}

export function asFigure(training) {
  const tagIcons = [];
  if (training.tagIcons) {
    training.tagIcons.forEach((tag) => {
      tagIcons.push({ name: tag.name, color: tag.color_hex });
    });
  }

  return {
    id: training.id,
    image: training.image,
    title: training.name,
    instructors: training.instructors
      ? training.instructors.map((instructor) => instructor.name)
      : [],
    duration: training.duration,
    isFavorite: training.isFavorite,
    isEnrolled: training.isEnrolled,
    price: training.price,
    priceWithDiscount: training.priceWithDiscount,
    preSaleEnabled: training.preSaleEnabled,
    preSaleExpired: training.preSaleExpired,
    preSaleDate: training.preSaleDate,
    subscriptionEnabled: training.subscriptionEnabled,
    subscriptionExpiresIn: training.subscriptionExpiresIn,
    discountPercentage: training.discountPercentage,
    userRating: training.userRating || null,
    ratingCount: training.ratingCount || null,
    progress: training.progress,
    link: getURL(training),
    tagIcons: tagIcons,
  };
}

export function asArticle(training) {
  return {
    id: training.id,
    title: training.name,
    thumbnail: training.image,
  };
}

export function useTrainings() {
  return useAPIList('/api/trainings');
}

export function useTraining(id, onlyTraining = false) {
  const [training, setTraining] = useState({
    id: id,
    name: 'Loading...',
    image: DefaultPreview,
    program: [],
    date: new Date(),
    endDate: new Date(),
    loading: true,
  });

  const [stands, setStands] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [sessions, setSessions] = useState({});
  const [tagIcons, setTagIcons] = useState({});

  useEffect(() => {
    fetchTraining(id)
      .then((x) => {
        const dateObject = new Date(x.preSaleDate);
        const formattedDateString = dateObject
          .toISOString()
          .slice(0, -5)
          .concat('Z');

        x.preSaleDate = formattedDateString;

        setTraining(x);
      })
      .catch(console.error);

    if (onlyTraining) return;
    fetchStands(id).then(setStands).catch(console.error);
    fetchPermissions(id).then(setPermissions).catch(console.error);
    fetchSessions(id).then(setSessions).catch(console.error);
    fetchTrainingTagIcons(id).then(setTagIcons).catch(console.error);
  }, [id, onlyTraining]);

  return [training, sessions, stands, permissions, tagIcons];
}

export function usePermissions(id) {
  const [permissions, setPermissions] = useState({});

  useEffect(() => {
    fetchPermissions(id).then(setPermissions).catch(console.error);
  }, [id]);

  return [permissions];
}

export function useSuggestedTrainings() {
  return AxiosInstance.get('/api/trainings/suggested');
}

export function fetchSuggestedTrainings() {
  return AxiosInstance.get('/api/trainings/suggested').then((res) => {
    return res.data;
  });
}

export function fetchSuggestedTrainingsWithTrainingId(trainingId) {
  return AxiosInstance.get(`/api/trainings/${trainingId}/suggested`).then(
    (res) => {
      return res.data;
    }
  );
}

export function fetchFavoritesTrainings() {
  return AxiosInstance.get('/api/trainings/favorites').then((res) => {
    return res.data;
  });
}

export function useAllTrainings() {
  const [trainings, setTrainings] = useState([]);

  useEffect(() => {
    fetchAllTrainings().then(setTrainings).catch(console.error);
  }, []);

  return [trainings];
}

export function fetchAllTrainings() {
  return AxiosInstance.get('/api/trainings?showAll=true').then((res) => {
    return res.data;
  });
}

export function fetchTrainings(highlight = false) {
  return AxiosInstance.get(`/api/trainings?highlight=${highlight}`).then(
    (res) => {
      return res.data;
    }
  );
}

export function fetchMyTrainings() {
  return AxiosInstance.get('/api/trainings/my').then((res) => {
    return res.data;
  });
}

/*export function fetchNextTrainings() {
    return fetchTrainings().then(trainings => trainings.filter(training => new Date(training.endDate) > new Date()));
}*/

export function addTraining(body) {
  return AxiosFileSender.post('/api/trainings', body).then((res) => res.data);
}

export function fetchTraining(id) {
  return AxiosInstance.get(`/api/trainings/${id}`).then((res) => res.data);
}

export function updateTraining(id, body) {
  body.preSaleDate = body.preSaleDate.concat('Z');
  return AxiosFileSender.put(`/api/trainings/${id}`, body).then(
    (res) => res.data
  );
}

export function deleteTraining(id) {
  return AxiosInstance.delete(`/api/trainings/${id}`).then((res) => res.data);
}

export function fetchTrainingsAvailable() {
  return AxiosInstance.get('/api/trainings/exist-for-language').then(
    (res) => res.data
  );
}

// Enrollments
export function enrollTraining(trainingId) {
  return AxiosInstance.post(`/api/trainings/${trainingId}/enrollment`);
}

export function useEnrollments(trainingId) {
  return useAPIList(`/api/trainings/${trainingId}/enrollment`);
}

export function useEnrollmentUsers(trainingId) {
  return useAPIList(`/api/trainings/${trainingId}/statistics/list`);
}

export function toggleCertificate(trainingId, userId, enable) {
  return AxiosInstance.put(
    `/api/trainings/${trainingId}/enrollment/${userId}/certificate`,
    {
      enable,
    }
  ).then((res) => res.data);
}

// Specialties
export function useSpecialties(trainingId) {
  const [items, , edit, deleteItem] = useAPIList(
    `/api/trainings/${trainingId}/specialties`
  );
  return [items, edit, deleteItem]; // edit is HTTP put with an id, so works as add
}

// Stands
export function useStands(trainingId) {
  const [items, , edit, deleteItem] = useAPIList(
    `/api/trainings/${trainingId}/stands`
  );
  return [items, edit, deleteItem]; // edit is HTTP put with an id, so works as add
}

export function fetchStands(trainingId) {
  return AxiosInstance.get(`/api/trainings/${trainingId}/stands`).then(
    (res) => res.data
  );
}

export function fetchPermissions(trainingId) {
  return AxiosInstance.get(`/api/trainings/${trainingId}/permissions`).then(
    (res) => res.data
  );
}

export function fetchTrainingTagIcons(trainingId) {
  return AxiosInstance.get(`/api/trainings/${trainingId}/tag-icons`).then(
    (res) => res.data
  );
}

//Tag Icons
export function useTrainingTagIcons(trainingId) {
  const [items, , edit, deleteItem] = useAPIList(
    `/api/trainings/${trainingId}/tag-icons`
  );
  return [items, edit, deleteItem]; // edit is HTTP put with an id, so works as add
}

//Tags
export function useTrainingTags(trainingId) {
  const [items, , edit, deleteItem] = useAPIList(
    `/api/trainings/${trainingId}/tags`
  );
  return [items, edit, deleteItem]; // edit is HTTP put with an id, so works as add
}

//Target Audience
export function useTrainingTargetAudience(trainingId) {
  const [items, , edit, deleteItem] = useAPIList(
    `/api/trainings/${trainingId}/target-audience`
  );
  return [items, edit, deleteItem]; // edit is HTTP put with an id, so works as add
}

export function fetchTrainingUsedTargetAudience() {
  return AxiosInstance.get(`/api/target-audience`).then((res) => res.data);
}

export function useTrainingUsedTargetAudience() {
  const [targetAudience, setTargetAudience] = useState([]);
  const { t } = useTranslation();

  useEffect(() => {
    fetchTrainingUsedTargetAudience()
      .then((data) => {
        data.push({ id: 0, name: t('trainings.trainings_all') });

        data.sort((a, b) => a.id - b.id);

        setTargetAudience(data);
      })

      .catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [targetAudience];
}

//Discounts
export function useTrainingDiscounts(trainingId) {
  const [items, , edit, deleteItem] = useAPIListAllReturn(
    `/api/trainings/${trainingId}/discounts`
  );
  return [items, edit, deleteItem]; // edit is HTTP put with an id, so works as add
}

//Instructors
export function useTrainingInstructors(trainingId) {
  const [items, , edit, deleteItem] = useAPIList(
    `/api/trainings/${trainingId}/instructors`
  );
  return [items, edit, deleteItem]; // edit is HTTP put with an id, so works as add
}

//Discounts
export function useTrainingSpecialties(trainingId) {
  const [items, , edit, deleteItem] = useAPIList(
    `/api/trainings/${trainingId}/specialties`
  );
  return [items, edit, deleteItem]; // edit is HTTP put with an id, so works as add
}

//Sessions
export function useSessions(trainingId) {
  const response = useAPIList(`/api/trainings/${trainingId}/sessions`);
  return response;
}

export function fetchActiveSessions(trainingId) {
  return AxiosInstance.get(
    `/api/trainings/${trainingId}/sessions?showAll=false`
  ).then((res) => res.data);
}

export function useActiveSessions(trainingId) {
  const [sessions, setSessions] = useState([]);
  const { t } = useTranslation();

  const finalQuiz = {
    id: 'finalQuiz',
    title: t('trainings.final_evaluation'),
    hidden: false,
    trainingId: 4,
    sessionContent: [
      {
        id: 'finalQuiz',
        sequenceNumber: 1,
        sessionId: 'finalQuiz',
        quizId: null,
        quizData: {},
        type: 'quiz',
      },
    ],
  };

  const fetchData = async () => {
    try {
      const sessionsData = await fetchActiveSessions(trainingId);
      setSessions(sessionsData);

      const quizData = await fetchTrainingQuiz(trainingId);

      finalQuiz.sessionContent[0].quizId = quizData.id;
      finalQuiz.sessionContent[0].quizData = quizData;

      const sessionsCopy = [...sessionsData];
      sessionsCopy.push(finalQuiz);
      setSessions(sessionsCopy);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trainingId]);

  return sessions;
}

export function fetchSessions(id) {
  return AxiosInstance.get(`/api/trainings/${id}/sessions`).then(
    (res) => res.data
  );
}

export function updateSession(sessionId, body) {
  return AxiosFileSender.put(`/api/trainings/sessions/${sessionId}`, body).then(
    (res) => res.data
  );
}

export function updateSessionSequence(trainingId, sessions) {
  return AxiosInstance.put(
    `/api/trainings/${trainingId}/sessions/sequence`,
    sessions
  ).then((res) => res.data);
}

export function updateSessionVideo(sessionId, videoId, body) {
  return AxiosFileSender.put(
    `/api/trainings/sessions/${sessionId}/videos/${videoId}/progress`,
    body
  ).then((res) => res.data);
}

export function fetchStatusVideo(sessionId, videoId) {
  return AxiosInstance.get(
    `/api/trainings/sessions/${sessionId}/videos/${videoId}/progress`
  ).then((res) => res.data);
}

export function useStatusVideo(sessionId, videoId) {
  const [sessionState, setSessionState] = useState({});

  useEffect(() => {
    fetchStatusVideo(sessionId, videoId)
      .then(setSessionState)
      .catch(console.error);
  }, [sessionId, videoId]);

  return sessionState;
}

//Ratings
export function useTrainingRatings(trainingId) {
  const [items, , edit, deleteItem] = useAPIList(
    `/api/trainings/${trainingId}/reviews`
  );
  return [items, edit, deleteItem]; // edit is HTTP put with an id, so works as add
}

export function fetchTrainingRatings(trainingId) {
  return AxiosInstance.get(`/api/trainings/${trainingId}/reviews`).then(
    (res) => {
      return res.data;
    }
  );
}

export function addTrainingRating(body) {
  return AxiosFileSender.post(
    `/api/trainings/${body.trainingId}/reviews`,
    body
  ).then((res) => res.data);
}

export function deleteSessionQuiz(sessionId, quizId) {
  return AxiosInstance.delete(
    `/api/trainings/sessions/${sessionId}/quizzes/${quizId}`
  ).then((res) => res.data);
}

export function deleteSessionVideo(sessionId, videoId) {
  return AxiosInstance.delete(
    `/api/trainings/sessions/${sessionId}/videos/${videoId}`
  ).then((res) => res.data);
}

export function useSessionVideo(sessionId, videoId) {
  return useAPIList(`/api/trainings/sessions/${sessionId}/videos/${videoId}`);
}

export function getSessionVideo(sessionId, videoId) {
  return AxiosInstance.get(
    `/api/trainings/sessions/${sessionId}/videos/${videoId}`
  ).then((res) => res.data);
}
//Statistics
export function fetchTrainingStatistics(
  trainingId,
  dates,
  additional_parameter
) {
  if (typeof additional_parameter === 'number') {
    return AxiosInstance.get(`/api/trainings/${trainingId}/statistics`, {
      params: { dates, country: additional_parameter },
    }).then((res) => {
      return res.data;
    });
  } else {
    return AxiosInstance.get(`/api/trainings/${trainingId}/statistics`, {
      params: { dates, currency: additional_parameter },
    }).then((res) => {
      return res.data;
    });
  }
}

export function addFavorite(trainingId) {
  return AxiosInstance.post(`/api/trainings/${trainingId}/favorites`).then(
    (res) => res.data
  );
}

export function fetchTrainingQuiz(trainingId) {
  return AxiosInstance.get(`/api/trainings/${trainingId}/quizzes`).then(
    (res) => res.data[0].finalQuiz[0]
  );
}

/*export function useTrainingQuiz(trainingId) {
  const [quiz, setQuiz] = useState({});

  useEffect(() => {
    fetchTrainingQuiz(trainingId)
      .then(setQuiz)
      .catch(console.error);
  }, [trainingId]);

  return quiz;
}*/

export function addTrainingQuiz(trainingId, body) {
  return AxiosFileSender.post(
    `/api/trainings/${trainingId}/quizzes`,
    body
  ).then((res) => res.data);
}

export function addTrainingQuestion(trainingId, body) {
  return AxiosFileSender.post(
    `/api/trainings/${trainingId}/questions`,
    body
  ).then((res) => res.data);
}
